import React, { createRef, useEffect, useImperativeHandle } from 'react'

import {
  Card,
  type CardData,
  type CardLabels,
  type CardProps,
  type CardTimeFormat,
} from '@/components/molecules/Card/Card.tsx'
import {
  ConfirmCompleteDialog,
  type ConfirmCompleteDialogAction,
  type ConfirmCompleteDialogLabels,
} from '@/components/molecules/ConfirmCompleteDialog/ConfirmCompletDialog.tsx'

import {
  ConfirmTeamDialog,
  type ConfirmTeamDialogAction,
  type ConfirmTeamDialogData,
  type ConfirmTeamDialogLabels,
  type ConfirmTeamDialogOptions,
} from '../ConfirmTeamDialog/ConfirmTeamDialog.tsx'

export type CardOnboardedTimeFormat = CardTimeFormat

export type CardOnboardedOptions = ConfirmTeamDialogOptions

export interface CardOnboardedLabels extends CardLabels {
  cancelConfirmDialog: ConfirmTeamDialogLabels
  arrivedAtDestinationConfirmDialog: ConfirmCompleteDialogLabels
}

export interface CardOnboardedState extends Pick<CardProps, 'size'> {
  state: 'default' | 'active'
  highlighted?: boolean
}

export interface CardOnboardedData extends Omit<CardData, 'team' | 'donation' | 'requestStatus'> {
  team: ConfirmTeamDialogData['team']
  pendingEvent?: 'cancelRequest' | 'arrivedAtDestination'
}

export interface CardOnboardedAction {
  onCancelRequest: ConfirmTeamDialogAction['onConfirm']
  onArrivedAtDestination: ConfirmCompleteDialogAction['onConfirm']
}

export interface CardOnboardedProps
  extends
    React.HtmlHTMLAttributes<HTMLDivElement>,
    CardOnboardedState,
    CardOnboardedData,
    CardOnboardedTimeFormat,
    CardOnboardedAction {
  labels: CardOnboardedLabels
  options: CardOnboardedOptions
}

export const CardOnboarded = React.forwardRef<HTMLDivElement, CardOnboardedProps>(
  (
    {
      state,
      size,
      request,
      status,
      team: { usingMobileApp, ...team },
      locationLabel,
      pendingEvent,
      labels,
      options,
      highlighted,
      timeAgoFormatter,
      onCancelRequest,
      onArrivedAtDestination,
      ...props
    },
    ref
  ) => {
    const cellRef = createRef<HTMLDivElement>()
    useImperativeHandle(ref, () => cellRef.current!, [cellRef])
    useEffect(() => {
      if (highlighted) {
        cellRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, [state, cellRef, highlighted])
    return (
      <Card
        className={highlighted ? 'animate-pulse-gray' : ''}
        state={state}
        size={size}
        labels={labels}
        request={request}
        requestStatus="onboarded"
        status={status}
        team={team}
        locationLabel={locationLabel}
        timeAgoFormatter={timeAgoFormatter}
        ref={ref}
        {...props}>
        {state === 'active' && (
          <>
            <ConfirmTeamDialog
              variant="critical"
              labels={labels.cancelConfirmDialog}
              options={options}
              team={{ usingMobileApp, ...team }}
              onConfirm={onCancelRequest}
              working={pendingEvent === 'cancelRequest'}
              disabled={!!pendingEvent && pendingEvent !== 'cancelRequest'}
            />

            <div className="flex h-full gap-2.5">
              <ConfirmCompleteDialog
                labels={labels.arrivedAtDestinationConfirmDialog}
                options={options}
                team={{ usingMobileApp, ...team }}
                onConfirm={onArrivedAtDestination}
                working={pendingEvent === 'arrivedAtDestination'}
                disabled={!!pendingEvent && pendingEvent !== 'arrivedAtDestination'}
              />
            </div>
          </>
        )}
      </Card>
    )
  }
)
